import { useSettingsStore } from "@/stores/settingsStore";
import { onActiveRootChange } from "@/services/drives";
import { logger } from "@/services/logger";
import { debounce } from "@/utils";
import { getConfigRepository } from "./index";

const log = logger.scope("config-sync");
const WRITE_DEBOUNCE_MS = 800;

/** Fields the `persist` middleware's own action-history noise shouldn't
 * round-trip through the drive-mirrored file — actions aren't state. */
type SettingsSnapshot = Omit<ReturnType<typeof useSettingsStore.getState>, "set" | "reset">;

function snapshot(): SettingsSnapshot {
  const {
    launchOnStartup,
    restoreLastSession,
    activeWorkspaceId,
    reduceMotion,
    compactDensity,
    showStatusBar,
    defaultModelId,
    defaultChatParams,
    embeddingModelId,
    favoriteFilePaths,
    ollamaHost,
    autoCheckUpdates,
    developerMode,
    telemetryEnabled,
    clipboardMonitoringEnabled,
    clipboardAutoCleanEnabled,
    clipboardSensitivePatterns,
    defaultPromptId,
    language,
    startupBehavior,
    voiceInputMode,
    pushToTalkHotkey,
    preferredMicDeviceId,
  } = useSettingsStore.getState();
  return {
    launchOnStartup,
    restoreLastSession,
    activeWorkspaceId,
    reduceMotion,
    compactDensity,
    showStatusBar,
    defaultModelId,
    defaultChatParams,
    embeddingModelId,
    favoriteFilePaths,
    ollamaHost,
    autoCheckUpdates,
    developerMode,
    telemetryEnabled,
    clipboardMonitoringEnabled,
    clipboardAutoCleanEnabled,
    clipboardSensitivePatterns,
    defaultPromptId,
    language,
    startupBehavior,
    voiceInputMode,
    pushToTalkHotkey,
    preferredMicDeviceId,
  };
}

async function loadFromActiveRoot(): Promise<void> {
  const repo = getConfigRepository();
  if (!repo) return;
  try {
    const stored = await repo.load();
    if (stored) {
      // A drive's own Config/settings.json wins on connect — that's the
      // portability story: bring the drive to another machine and its
      // settings travel with it.
      useSettingsStore.setState(stored as Partial<SettingsSnapshot>);
    }
  } catch (err) {
    log.warn("Failed to load settings from the active root; keeping local settings.", err);
  }
}

const writeToActiveRoot = debounce(() => {
  const repo = getConfigRepository();
  if (!repo) return;
  void repo.save(snapshot()).catch((err: unknown) => log.warn("Failed to mirror settings", err));
}, WRITE_DEBOUNCE_MS);

let started = false;

/** Wires `settingsStore` to the active root's `Config/settings.json`: loads
 * it once at startup (and again on every drive connect), and mirrors every
 * change back to it, debounced. `localStorage` (via the store's own
 * `persist` middleware) stays authoritative for the very first paint and
 * for browser-dev/no-drive use. */
export function initConfigSync(): void {
  if (started) return;
  started = true;

  void loadFromActiveRoot();
  useSettingsStore.subscribe(() => writeToActiveRoot());
  onActiveRootChange(() => void loadFromActiveRoot());
}
